/**
 * F20 (Wave 2D) — admin payments activity-feed API client.
 *
 * Endpoint:
 *   GET /admin/payments  → paginated `payment_marks` ledger across all venues
 *
 * Filters (Decision 8 + Decision 17): venue, method, time range, actor_kind,
 * to_status, overdue. The `overdue=true` flag adds the
 * `bookings.status='completed' AND bookings.payment_status='unpaid'` predicate
 * server-side; F20 does NOT introduce a worker — overdue is a filter, not a
 * workflow.
 *
 * Permission: F06 `payments:read` (super_admin + support + finance).
 * Trust-based marking (Decision 1) → admin queue here is INFORMATIONAL only.
 */
import type {
  AdminPageMeta,
  PaymentMark,
  PaymentMarkActorKind,
  PaymentMethod,
  PaymentStatus,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

// ─── Response envelopes ─────────────────────────────────────────────────────

export interface PaymentActivityBookingRef {
  readonly id: string;
  readonly venueId: string;
  /** Joined venue display name; null when the venue row is gone. */
  readonly venueName: string | null;
  readonly status: string;
  readonly paymentStatus: PaymentStatus;
  readonly priceAmount: number;
  readonly priceCurrency: string;
}

export interface PaymentActivityRow extends PaymentMark {
  readonly booking: PaymentActivityBookingRef | null;
}

export interface PaymentActivityResponse {
  readonly items: readonly PaymentActivityRow[];
  readonly nextCursor: string | null;
  readonly total?: number;
  /** Present when `page` is supplied (page-numbered pagination). */
  readonly pagination?: AdminPageMeta;
}

// ─── List options ───────────────────────────────────────────────────────────

export interface PaymentActivityOpts {
  readonly venueId?: string;
  readonly method?: PaymentMethod;
  readonly actorKind?: PaymentMarkActorKind;
  readonly toStatus?: PaymentStatus;
  /** ISO 8601 with timezone offset; the server validates. */
  readonly dateFrom?: string;
  readonly dateTo?: string;
  /** Decision 8 — filter, not workflow. */
  readonly overdue?: boolean;
  readonly cursor?: string;
  readonly limit?: number;
  /** 1-based page index (page-numbered pagination). */
  readonly page?: number;
  /** Page size (default 25 server-side, max 100). */
  readonly perPage?: number;
}

function buildActivityQuery(opts: PaymentActivityOpts): string {
  return buildQuery({
    venueId: opts.venueId,
    method: opts.method,
    actorKind: opts.actorKind,
    toStatus: opts.toStatus,
    dateFrom: opts.dateFrom,
    dateTo: opts.dateTo,
    overdue: opts.overdue,
    cursor: opts.cursor,
    limit: opts.limit,
    page: opts.page,
    perPage: opts.perPage,
  });
}

// ─── API surface ────────────────────────────────────────────────────────────

export const adminPaymentsApi = {
  /** GET /admin/payments — paginated activity feed. */
  listActivity: async (opts?: PaymentActivityOpts): Promise<PaymentActivityResponse> => {
    // The API returns the rows under `data` (not `items`); map to the
    // consumer-side `{ items, nextCursor, pagination }` shape.
    const raw = await adminFetch<{
      data?: readonly PaymentActivityRow[];
      items?: readonly PaymentActivityRow[];
      nextCursor: string | null;
      total?: number;
      pagination?: AdminPageMeta;
    }>(`/admin/payments${buildActivityQuery(opts ?? {})}`);
    return {
      items: raw.data ?? raw.items ?? [],
      nextCursor: raw.nextCursor,
      ...(raw.total !== undefined ? { total: raw.total } : {}),
      ...(raw.pagination !== undefined ? { pagination: raw.pagination } : {}),
    };
  },
};
